import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from './store'
import type { ConversationModel, MessageModel, PhoneCallModel } from './conversationsSlice'
import type { ExpenseModel } from './expensesSlice'
import { isUnsyncable, type SyncSliceState } from './syncSlice'

// Alice conversation constants. Defined in lib/conversations/aliceTopicTypes so
// that lib/sync can use them without importing from app/ -- re-exported here so
// the many existing `from './conversationsSelectors'` imports keep resolving.
export {
  ALICE_TOPIC_TYPE,
  ALICE_PROVIDER_TOPIC_TYPE,
  ALICE_ANSWERED_TOPIC_TYPES,
  ITEMIZED_BILL_TOPIC_TYPES,
} from '../lib/conversations/aliceTopicTypes'

import { ALICE_TOPIC_TYPE, ITEMIZED_BILL_TOPIC_TYPES } from '../lib/conversations/aliceTopicTypes'

// Base selectors
export const selectAllConversations = (state: RootState): ConversationModel[] =>
  state.conversations?.conversations || []

export const selectAllMessages = (state: RootState): MessageModel[] =>
  state.conversations?.messages || []

export const selectAllPhoneCalls = (state: RootState): PhoneCallModel[] =>
  state.conversations?.phoneCalls || []

// Conversation selectors
export const selectConversationById = createSelector(
  [selectAllConversations, (_state: RootState, conversationId: string) => conversationId],
  (conversations, conversationId) => conversations.find((c) => c.id === conversationId)
)

export const selectConversationsForProvider = createSelector(
  [selectAllConversations, (_state: RootState, providerId: string) => providerId],
  (conversations, providerId) =>
    conversations
      .filter((c) => c.provider_id === providerId)
      .sort((a, b) => {
        // Sort by last_message_at descending (most recent first)
        const aTime = a.last_message_at || a.created_at
        const bTime = b.last_message_at || b.created_at
        return bTime.localeCompare(aTime)
      })
)

/**
 * The itemized-bill conversation for one expense.
 *
 * `topic_ref_id` holds the **incident** id, not the expense id. Every backend path writes it
 * that way -- RequestItemizedBillsJob ("using incident_id as topic_ref_id for proper
 * permissions"), AiCallController::store, TodosList -- and AiEmailConversationMatcher reads it
 * back with `Incident::find($conversation->topic_ref_id)`. There is a unique index on
 * (membership_id, provider_id, topic_type, topic_ref_id), so one incident and provider get one
 * thread however the request was raised.
 *
 * The React AI-call screen used to write `topic_ref_id = expenseId` instead, and three separate
 * lookups here matched it that way. That is why this is one exported function rather than a
 * find() copied into each caller: the copies disagreed, and the disagreement was invisible
 * until an admin-initiated call and a member-initiated one produced two threads.
 * `20260826000000_rekey_itemized_bill_conversations_to_incident.sql` re-keys the rows that were
 * written the old way.
 *
 * `expenses.itemized_bill_conversation_id` is the more direct link and wins where it is set;
 * the incident match is the fallback for threads created before it was populated.
 *
 * Both itemized-bill topic types match. RequestItemizedBillsJob picks between them by feature
 * flag, so an organization with Alice keys the very same thread under `alice-provider-chat` --
 * see ITEMIZED_BILL_TOPIC_TYPES.
 */
export function findItemizedBillConversation<C extends ConversationModel>(
  conversations: C[],
  expense: Pick<ExpenseModel, 'incident_id' | 'itemized_bill_conversation_id'> | undefined,
  providerId: string | null | undefined,
): C | undefined {
  if (!expense) { return undefined }

  if (expense.itemized_bill_conversation_id) {
    const linked = conversations.find((c) => c.id === expense.itemized_bill_conversation_id)
    if (linked) { return linked }
  }

  if (!expense.incident_id || !providerId) { return undefined }

  // `provider_id` is part of the match, not just of the caller's pre-filtering. The unique
  // index is (membership_id, provider_id, topic_type, topic_ref_id), so one incident may
  // legally carry a thread per provider -- and a caller that hands in a membership-wide list
  // (expenseTable does) would otherwise get whichever provider's thread happened to be first.
  return conversations.find(
    (c) =>
      c.provider_id === providerId &&
      ITEMIZED_BILL_TOPIC_TYPES.has(c.topic_type) &&
      c.topic_ref_id === expense.incident_id
  )
}

// Message selectors
export const selectMessagesForConversation = createSelector(
  [selectAllMessages, (_state: RootState, conversationId: string) => conversationId],
  (messages, conversationId) =>
    messages
      .filter((m) => m.conversation_id === conversationId && !m.deleted_at)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
)

/**
 * How far a message the member composed on this device has got towards the server.
 *
 * - `sending`   — still sitting in the sync queue, so Alice can't see it yet.
 * - `delivered` — the server has the row (either sync-up applied it, or it came
 *                 down from the server in the first place).
 * - `failed`    — sync gave up on it (see lib/sync/unrecoverable.ts); it will
 *                 never reach the server without the member sending it again.
 */
export type MessageDeliveryStatus = 'sending' | 'delivered' | 'failed'

/**
 * Delivery status of a single message, derived from the sync queue.
 *
 * Only the `add` action matters: once the insert has landed, the body of the
 * message is on the server even if a later update (a read receipt, say) is
 * still queued.  A message with no queue entry at all has either synced or was
 * never composed here, both of which mean the server has it.
 */
export function selectMessageDeliveryStatus(
  state: RootState,
  message: Pick<MessageModel, 'id' | 'updated_at'>
): MessageDeliveryStatus {
  // Persisted state written before `unsyncable` existed has no such key, and
  // Storybook/test states may omit the slice entirely.
  const sync: Partial<SyncSliceState> = state.sync || {}

  if (isUnsyncable(sync, 'messages', message)) {
    return 'failed'
  }

  const queued = sync.pending?.some(
    (p) => p.type === 'add' && p.table === 'messages' && p.record.id === message.id
  )

  return queued ? 'sending' : 'delivered'
}

// Phone call selectors
export const selectPhoneCallsForConversation = createSelector(
  [selectAllPhoneCalls, (_state: RootState, conversationId: string) => conversationId],
  (phoneCalls, conversationId) =>
    phoneCalls
      .filter((p) => p.conversation_id === conversationId && !p.deleted_at)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
)

// Combined timeline selector
export type TimelineItem =
  | { type: 'message'; data: MessageModel }
  | { type: 'phone_call'; data: PhoneCallModel }

export const selectConversationTimeline = createSelector(
  [
    selectMessagesForConversation,
    selectPhoneCallsForConversation,
  ],
  (messages, phoneCalls): TimelineItem[] => {
    const timeline: TimelineItem[] = [
      ...messages.map((m) => ({ type: 'message' as const, data: m })),
      ...phoneCalls.map((p) => ({ type: 'phone_call' as const, data: p })),
    ]

    return timeline.sort((a, b) => a.data.created_at.localeCompare(b.data.created_at))
  }
)

// Full conversation with nested data (similar to useConversationData hook's return)
export interface ConversationWithData {
  conversation: ConversationModel
  messages: MessageModel[]
  phoneCalls: PhoneCallModel[]
  timeline: TimelineItem[]
}

export const selectConversationWithData = createSelector(
  [
    selectConversationById,
    selectMessagesForConversation,
    selectPhoneCallsForConversation,
    selectConversationTimeline,
  ],
  (conversation, messages, phoneCalls, timeline): ConversationWithData | null => {
    if (!conversation) {
      return null
    }

    return {
      conversation,
      messages,
      phoneCalls,
      timeline,
    }
  }
)

// Helper to get the most recent activity (message or call) for a conversation
export const selectMostRecentActivityForConversation = createSelector(
  [selectConversationTimeline],
  (timeline): TimelineItem | null => {
    if (timeline.length === 0) {
      return null
    }
    return timeline[timeline.length - 1]
  }
)

// Backward compatibility type - matches the old useConversationData hook structure
export type ConversationWithMessagesAndPhoneCalls = ConversationModel & {
  messages: MessageModel[]
  phone_calls: PhoneCallModel[]
}

// Selector that returns conversations with nested messages and phone_calls (for backward compatibility)
export const selectConversationsWithNestedDataForProvider = createSelector(
  [selectAllConversations, selectAllMessages, selectAllPhoneCalls, (_state: RootState, providerId: string) => providerId],
  (conversations, allMessages, allPhoneCalls, providerId): ConversationWithMessagesAndPhoneCalls[] => {
    return conversations
      .filter((c) => c.provider_id === providerId)
      .map((conversation) => {
        const messages = allMessages.filter(
          (m) => m.conversation_id === conversation.id && !m.deleted_at
        )
        const phone_calls = allPhoneCalls.filter(
          (p) => p.conversation_id === conversation.id && !p.deleted_at
        )

        return {
          ...conversation,
          messages,
          phone_calls,
        }
      })
      .sort((a, b) => {
        // Sort by last_message_at descending (most recent first)
        const aTime = a.last_message_at || a.created_at
        const bTime = b.last_message_at || b.created_at
        return bTime.localeCompare(aTime)
      })
  }
)

// ============ Alice Conversation Selectors ============

// Check if a conversation is an Alice conversation
export function isAliceConversation(conversation: ConversationModel): boolean {
  return conversation.topic_type === ALICE_TOPIC_TYPE
}

// Select all Alice conversations sorted by most recent first
export const selectAliceConversations = createSelector(
  [selectAllConversations],
  (conversations): ConversationModel[] => {
    return conversations
      .filter(isAliceConversation)
      .sort((a, b) => {
        const aTime = a.last_message_at || a.created_at
        const bTime = b.last_message_at || b.created_at
        return bTime.localeCompare(aTime)
      })
  }
)

// Select Alice conversations with their messages
export const selectAliceConversationsWithMessages = createSelector(
  [selectAliceConversations, selectAllMessages],
  (conversations, allMessages) => {
    return conversations.map((conversation) => {
      const messages = allMessages
        .filter((m) => m.conversation_id === conversation.id && !m.deleted_at)
        .sort((a, b) => a.created_at.localeCompare(b.created_at))

      return {
        ...conversation,
        messages,
      }
    })
  }
)
